import React, {useState, useEffect} from 'react';
import CronParser from 'cron-parser';
import cronstrue from 'cronstrue';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faEraser } from '@fortawesome/free-solid-svg-icons';

const CronJobParser = () => {
  const [timeZone, setTimeZone] = useState('Asia/Ho_Chi_Minh');
  const [expression, setExpression] = useState('*/5 * * * *');
  const [humanText, setHumanText] = useState('');
  const [prexRun, setPrevRun] = useState('');
  const [nextRun, setNextRun] = useState('');
  const [fields, setFields] = useState();
  const dayOfWeekNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const showErrorMessage = (errorMessage) => {
    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  }

  const handleParse = () => {
    try {
      const cronExpression = expression.split(' ').slice(0, 5).join(' ');
      const timeZoneExpression = expression.split(' ').slice(5).join(' ');
      const options = {
        currentDate: new Date(),
        tz: timeZoneExpression || timeZone,
      };
      const interval = CronParser.parseExpression(cronExpression, options);
      setPrevRun(interval.prev().toString());
      setNextRun(interval.next().toString());
      setFields(JSON.parse(JSON.stringify(interval.fields)));
      setHumanText(cronstrue.toString(cronExpression, { verbose: true }));
    } catch (err) {
      console.error('Error parsing cron expression:', err);
      showErrorMessage('Error parsing cron expression');
    }
  }
  const renderFieldValue = (field) => {
    const value = fields[field];
    switch (field) {
      case 'hour':
        if (value.length > 23) {
          return 'All';
        }
        return value.join(', ');
      case 'dayOfWeek':
        if (value.length > 6) {
          return 'All';
        }
        return value.map((v) => dayOfWeekNames[v]).join(', ');
      case 'dayOfMonth':
        if (value.length > 28) {
          return 'All';
        }
        return value.join(', ');
      case 'month':
        if (value.length === 12) {
          return 'All';
        }
        break;
      default:
        return value.join(', ');
    }
  }
  const underscoreToTitle = (str) => {
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  useEffect(() => {
    if (expression === '') {
      setHumanText(null)
    } else {
      handleParse()
    }
  }, [expression]);
  return (
    <div className="grid grid-cols-1 my-4 xl:grid-cols-2 xl:gap-4">
      <div className="col-span-2 xl:col-auto">
        <div className="h-screen p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-blue-600 p-2'>Input</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <input onChange={(e) => setExpression(e.target.value)} value={expression} type="text" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Cron expression" />
          </div>
          <div className="px-4 py-2 text-gray-400rounded dark:border-gray-600">
            <button
              type="button"
              onClick={() => setExpression('')}
              className="text-white bg-gray-400 hover:bg-gray-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faEraser}/>
              &nbsp; Reset
            </button>
            &nbsp;&nbsp;
            <button
              onClick={handleParse}
              type="button"
              className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faPaperPlane}/>
              &nbsp; Run
            </button>
          </div>
        </div>
      </div>

      <div className="col-span-2 xl:col-auto">
        <div className="h-screen p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-yellow-600 p-2'>Output Result</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            { expression && fields && (
              <table  className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                <tbody>
                  <tr className='bg-white border-b dark:bg-gray-800 dark:border-gray-700'>
                    <th className="px-6 py-4">Human Text</th>
                    <td className="px-6 py-4">{humanText}</td>
                  </tr>
                  <tr className='bg-white border-b dark:bg-gray-800 dark:border-gray-700'>
                    <th className="px-6 py-4">Your Local Timezone</th>
                    <td className="px-6 py-4">{timeZone}</td>
                  </tr>
                  <tr className='bg-white border-b dark:bg-gray-800 dark:border-gray-700'>
                    <th className="px-6 py-4">Previous Run At</th>
                    <td className="px-6 py-4">{prexRun}</td>
                  </tr>
                  <tr className='bg-white border-b dark:bg-gray-800 dark:border-gray-700'>
                    <th className="px-6 py-4">Next Run At</th>
                    <td className="px-6 py-4">{nextRun}</td>
                  </tr>
                  {fields &&
                    Object.keys(fields).map((field) => (
                      <tr key={field} className='bg-white border-b dark:bg-gray-800 dark:border-gray-700'>
                        <th className="px-6 py-4">{underscoreToTitle(field)}</th>
                        <td className="px-6 py-4">{renderFieldValue(field)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) }
          </div>
        </div>
      </div>
    </div>
  );
};

export default CronJobParser;
